import React from "react";
import ReactDOM from "react-dom/client";
import { ErrorBoundary } from "react-error-boundary";
import ReactGA from "react-ga4";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { AppModeProvider } from "src/contexts/app-mode";

import { ErrorMessageModal } from "@components/shared/ErrorMessageModal/ErrorMessageModal";
import { COOKIE_CONSENT_KEY, COOKIE_CONSENT_VALUES } from "@constants/cookies";
import { STORAGE_KEYS } from "@constants/Kiosk";
import type { AppMode } from "@models/components/kiosk.model";

import { initializeFirebaseSW } from "./config/firebase";
import { PostHogProviderWrapper } from "./providers/PostHogProvider";
import { persistor, store } from "./store/index";
import { getCookie } from "./utils/common.utils";
import App from "./App";
import { GOOGLE_ANALYTICS_KEY } from "./app.config";

import "bootstrap/dist/css/bootstrap.min.css";
import "./styles.scss";

const cookieConsent = getCookie(COOKIE_CONSENT_KEY);

if (cookieConsent === COOKIE_CONSENT_VALUES.ALL) {
  ReactGA.initialize(GOOGLE_ANALYTICS_KEY);
}

initializeFirebaseSW();

sessionStorage.removeItem(STORAGE_KEYS.LANDING_REDIRECTED);

import "@/config/sentry";

const getInitialMode = (): AppMode => {
  const envMode = import.meta.env.VITE_APP_TARGET;
  return envMode === "kiosk" ? "kiosk" : "web";
};

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ErrorBoundary
      fallbackRender={({ error, resetErrorBoundary }) => (
        <ErrorMessageModal
          error={error}
          resetErrorBoundary={resetErrorBoundary}
        />
      )}
    >
      <Provider store={store}>
        <PersistGate persistor={persistor} loading={null}>
          <AppModeProvider initialMode={getInitialMode()}>
            <PostHogProviderWrapper>
              <App />
            </PostHogProviderWrapper>
          </AppModeProvider>
        </PersistGate>
      </Provider>
    </ErrorBoundary>
  </React.StrictMode>
);
